"use client";

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { fetchProducts, fetchCategories } from "@/lib/woocommerce";
import { WooCommerceProduct, ProductFilters, WooCommerceCategory } from "@/types/woocommerce";

// Helper function to check if product matches attribute filter
function matchesAttributeFilter(
  product: WooCommerceProduct,
  attributeName: string,
  selectedValues: string[] | undefined
): boolean {
  if (!selectedValues || selectedValues.length === 0) return true;

  // Map our internal attribute names to WooCommerce attribute names
  const attributeNameMap: Record<string, string> = {
    'gorivo': 'fuel',
    'frekvencija': 'frequency', 
    'faza': 'Phase',
    'emisija': 'Emission',
    'snaga': 'Power (PRP / ESP)'
  };

  const wooAttributeName = attributeNameMap[attributeName] || attributeName;
  
  const attribute = product.attributes.find(
    (attr) => attr.name === wooAttributeName
  );

  if (!attribute) return false;

  // Check if any of the selected values match the product's attribute options
  return selectedValues.some((value) =>
    attribute.options.some((opt) => opt.toLowerCase().includes(value.toLowerCase()))
  );
}

// Helper function to extract numeric value from attribute
function getAttributeNumberValue(product: WooCommerceProduct, attributeName: string): number | null {
  // Map our internal attribute names to WooCommerce attribute names
  const attributeNameMap: Record<string, string> = {
    'gorivo': 'fuel',
    'frekvencija': 'frequency', 
    'faza': 'Phase',
    'emisija': 'Emission',
    'snaga': 'Power (PRP / ESP)'
  };

  const wooAttributeName = attributeNameMap[attributeName] || attributeName;
  
  const attribute = product.attributes.find(
    (attr) => attr.name === wooAttributeName
  );

  if (!attribute || attribute.options.length === 0) return null;

  // Try to parse the first option as a number
  const value = parseFloat(attribute.options[0].replace(/[^0-9.]/g, ""));
  return isNaN(value) ? null : value;
}

// Client-side filter function
function filterProductsByAttributes(
  products: WooCommerceProduct[],
  filters: ProductFilters
): WooCommerceProduct[] {
  return products.filter((product) => {
    // Exclude products from certain categories
    if (filters.excludedCategories && filters.excludedCategories.length > 0) {
      const hasExcludedCategory = product.categories.some(
        (cat) => filters.excludedCategories!.includes(cat.id)
      );
      if (hasExcludedCategory) return false;
    }

    // Gorivo (Fuel) filter
    if (filters.gorivo && filters.gorivo.length > 0) {
      if (!matchesAttributeFilter(product, "gorivo", filters.gorivo)) return false;
    }

    // Frekvencija (Frequency) filter
    if (filters.frekvencija && filters.frekvencija.length > 0) {
      if (!matchesAttributeFilter(product, "frekvencija", filters.frekvencija)) return false;
    }

    // Faza (Phase) filter
    if (filters.faza && filters.faza.length > 0) {
      if (!matchesAttributeFilter(product, "faza", filters.faza)) return false;
    }

    // Emisija (Emission) filter
    if (filters.emisija && filters.emisija.length > 0) {
      if (!matchesAttributeFilter(product, "emisija", filters.emisija)) return false;
    }

    // Snaga (Power) range filter
    if (filters.minSnaga !== undefined || filters.maxSnaga !== undefined) {
      const snagaValue = getAttributeNumberValue(product, "snaga");
      if (snagaValue !== null) {
        if (filters.minSnaga !== undefined && snagaValue < filters.minSnaga) return false;
        if (filters.maxSnaga !== undefined && snagaValue > filters.maxSnaga) return false;
      }
    }

    return true;
  });
}

// Extract only API-relevant filters
function getApiFilters(filters: ProductFilters) {
  return {
    search: filters.search,
    category: filters.category,
    minPrice: filters.minPrice,
    maxPrice: filters.maxPrice,
    onSale: filters.onSale,
    inStock: filters.inStock,
    orderby: filters.orderby,
  };
}

export function useProducts(filters: ProductFilters) {
  const [allProducts, setAllProducts] = useState<WooCommerceProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialLoad, setInitialLoad] = useState(true);

  // Keep track of last API filters to avoid duplicate fetches
  const lastApiFiltersRef = useRef<string>("");

  const apiFilters = getApiFilters(filters);
  const apiFiltersKey = JSON.stringify(apiFilters);

  const fetchData = useCallback(async () => {
    // Only fetch if API filters changed
    if (apiFiltersKey === lastApiFiltersRef.current) {
      return;
    }

    setLoading(true);
    setError(null);
    setInitialLoad(true);

    try {
      const allFetchedProducts: WooCommerceProduct[] = [];
      let page = 1;
      const perPage = 100;
      let hasMore = true;

      // Fetch first page quickly for initial display
      const firstPageParams: Record<string, string | number | boolean> = {
        per_page: perPage,
        page: 1,
      };

      // Add API filters to first page
      if (apiFilters.search) firstPageParams.search = apiFilters.search;
      if (apiFilters.category) firstPageParams.category = apiFilters.category;
      if (apiFilters.minPrice !== undefined) firstPageParams.min_price = apiFilters.minPrice;
      if (apiFilters.maxPrice !== undefined) firstPageParams.max_price = apiFilters.maxPrice;
      if (apiFilters.onSale) firstPageParams.on_sale = true;
      if (apiFilters.inStock) firstPageParams.stock_status = "instock";
      if (apiFilters.orderby) {
        firstPageParams.orderby = apiFilters.orderby;
        if (apiFilters.orderby === "price") {
          firstPageParams.order = "asc";
        } else if (apiFilters.orderby === "price-desc") {
          firstPageParams.orderby = "price";
          firstPageParams.order = "desc";
        }
      }

      // Add auth credentials
      firstPageParams.consumer_key = "ck_50493b2d57ff0f3895567b202c207e1bd36dbac7";
      firstPageParams.consumer_secret = "cs_8dd00d08612e5c652cee2b27aeaded98e032d756";

      const queryParams = new URLSearchParams(firstPageParams as any);
      const firstPageUrl = `${"https://tehnolink.net/wp-json/wc/v3"}/products?${queryParams.toString()}`;
      
      const firstPageResponse = await fetch(firstPageUrl, {
        headers: { "Content-Type": "application/json" }
      });

      if (!firstPageResponse.ok) {
        throw new Error(`Failed to fetch products: ${firstPageResponse.statusText}`);
      }

      const firstPageData = await firstPageResponse.json();
      allFetchedProducts.push(...firstPageData);
      setAllProducts(allFetchedProducts); // Show first page immediately
      setInitialLoad(false);

      // Continue fetching remaining pages in background
      if (firstPageData.length === perPage) {
        page = 2;
        while (hasMore) {
          const params: Record<string, string | number | boolean> = {
            per_page: perPage,
            page: page,
            consumer_key: "ck_50493b2d57ff0f3895567b202c207e1bd36dbac7",
            consumer_secret: "cs_8dd00d08612e5c652cee2b27aeaded98e032d756",
          };

          // Only use API filters that WooCommerce supports
          if (apiFilters.search) params.search = apiFilters.search;
          if (apiFilters.category) params.category = apiFilters.category;
          if (apiFilters.minPrice !== undefined) params.min_price = apiFilters.minPrice;
          if (apiFilters.maxPrice !== undefined) params.max_price = apiFilters.maxPrice;
          if (apiFilters.onSale) params.on_sale = true;
          if (apiFilters.inStock) params.stock_status = "instock";
          if (apiFilters.orderby) {
            params.orderby = apiFilters.orderby;
            if (apiFilters.orderby === "price") {
              params.order = "asc";
            } else if (apiFilters.orderby === "price-desc") {
              params.orderby = "price";
              params.order = "desc";
            }
          }

          const queryParams = new URLSearchParams(params as any);
          const url = `${"https://tehnolink.net/wp-json/wc/v3"}/products?${queryParams.toString()}`;

          const data = await fetch(url, {
            headers: { "Content-Type": "application/json" }
          }).then(res => {
            if (!res.ok) throw new Error(`Failed to fetch products: ${res.statusText}`);
            return res.json();
          });

          allFetchedProducts.push(...data);
          setAllProducts([...allFetchedProducts]); // Update with additional products

          // Check if we got fewer products than requested (last page)
          if (data.length < perPage) {
            hasMore = false;
          } else {
            page++;
          }
        }
      }

      lastApiFiltersRef.current = apiFiltersKey;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch products");
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [apiFiltersKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Apply client-side attribute filtering instantly (no API calls)
  const filteredProducts = useMemo(() => {
    return filterProductsByAttributes(allProducts, filters);
  }, [allProducts, filters]);

  return { products: filteredProducts, loading, error, refetch: fetchData, initialLoad };
}

export function useCategories() {
  const [categories, setCategories] = useState<WooCommerceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const data = await fetchCategories();
        setCategories(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch categories");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  return { categories, loading, error };
}
